import React from "react";
import { formatBedroomOptions } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { EnhancedImage } from "@/components/shared/EnhancedImage";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Bed, Bath, Square, Phone, Eye, IndianRupee, Rocket, Clock } from "lucide-react";
import { AuthGatedDialog } from "@/components/shared/AuthGatedDialog";
import { WhatsAppShare } from "@/components/shared/WhatsAppShare";
import { getPossessionStageLabel } from "@/components/public/PossessionStageFilter";

import { EmailShare } from "@/components/shared/EmailShare";
import { PropertyShare } from "@/components/shared/PropertyShare";
import { FavoriteButton } from "@/components/shared/FavoriteButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { InquiryForm } from "./InquiryForm";
import { PriceRequestForm } from "./PriceRequestForm";
import { Link } from "@/lib/router-compat";
import { formatPriceDisplay } from "@/utils/currency";
import { getPropertyUrl } from "@/utils/slugify";
import { ListingTypeBadge, normalizeListingType } from "@/components/shared/ListingTypeBadge";
import { RentPriceSuffix } from "@/components/shared/RentPriceSuffix";

// Import default images
import defaultResidentialImage from "@/assets/default-residential-property.webp";
import defaultCommercialImage from "@/assets/default-commercial-property.webp";

interface Property {
  id: string;
  slug?: string | null;
  title: string;
  location?: string | null;
  locality?: string | null;
  city: string | null;
  address?: string | null;
  price: number | null;
  price_min?: number | null;
  price_max?: number | null;
  property_type: string;
  area_sqft?: number | null;
  area_min?: number | null;
  area_max?: number | null;
  bedrooms?: number | null;
  bedrooms_options?: number[];
  bathrooms?: number | null;
  images?: string[] | null;
  description?: string | null;
  is_featured: boolean | null;
  price_on_request?: boolean;
  rera_number?: string | null;
  floor_number?: number | null;
  unit_number?: string | null;
  total_floors?: number | null;
  developer_name?: string | null;
  possession_stage?: string | null;
  listing_type?: string | null;
}

interface PropertyCardProps {
  property: Property;
}

export const PropertyCard = ({ property }: PropertyCardProps) => {
// Price formatting handled via utils

  const getPropertyTypeLabel = (type: string) => {
    return type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getDefaultImage = (propertyType: string) => {
    const isCommercial = propertyType.toLowerCase().includes('commercial') || 
                        propertyType.toLowerCase().includes('office') || 
                        propertyType.toLowerCase().includes('retail') ||
                        propertyType.toLowerCase().includes('warehouse') ||
                        propertyType.toLowerCase().includes('industrial');
    
    return isCommercial ? defaultCommercialImage : defaultResidentialImage;
  };

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow relative">
      <div className="absolute top-2 left-2 z-20 flex flex-wrap gap-2">
        <ListingTypeBadge listingType={property.listing_type} />
        {property.is_featured && (
          <Badge className="bg-primary">
            Featured
          </Badge>
        )}
        {property.possession_stage === 'new-launch' && (
          <Badge className="bg-emerald-600 text-white">
            <Rocket className="h-3 w-3 mr-1" />
            New Launch
          </Badge>
        )}
        {property.possession_stage === 'under-construction' && (
          <Badge className="bg-amber-500 text-white">
            <Clock className="h-3 w-3 mr-1" />
            Under Construction
          </Badge>
        )}
      </div>
      
      <div className="absolute top-2 right-2 z-20">
        <FavoriteButton propertyId={property.id} />
      </div>
      
      <div className="relative h-48 bg-muted">
        <EnhancedImage 
          src={property.images && property.images.length > 0 ? property.images[0] : getDefaultImage(property.property_type)} 
          alt={property.title}
          className="w-full h-full object-cover"
          enableEnhancement={!!(property.images && property.images.length > 0)}
          optimizedWidth={480}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
          fallbackSrc={getDefaultImage(property.property_type)}
        />
      </div>

      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-semibold text-lg truncate">
            <Link to={getPropertyUrl(property)} aria-label={`View details for ${property.title}`} className="hover:underline focus:outline-none">
              {property.title}
            </Link>
          </h3>
          <Badge variant="secondary">{getPropertyTypeLabel(property.property_type)}</Badge>
        </div>
        
        <div className="flex items-center text-muted-foreground text-sm mb-1">
          <MapPin className="h-3 w-3 mr-1 shrink-0" />
          <span className="truncate">{property.location || property.locality || ''}{(property.location || property.locality) && property.city ? ', ' : ''}{property.city || ''}</span>
        </div>
        {property.address && (
          <p className="text-xs text-muted-foreground mb-2 truncate pl-4">{property.address}</p>
        )}

<div className="text-2xl font-bold text-primary mb-3">
          {formatPriceDisplay({
            price_on_request: property.price_on_request,
            price: property.price,
            price_min: property.price_min ?? null,
            price_max: property.price_max ?? null,
          })}
          <RentPriceSuffix listingType={property.listing_type} priceOnRequest={property.price_on_request} />
        </div>

        <div className="flex gap-4 text-sm text-foreground mb-3">
          {(property.area_min != null && property.area_max != null) ? (
            <div className="flex items-center">
              <Square className="h-3 w-3 mr-1" />
              <span>{property.area_min} - {property.area_max} sq ft</span>
            </div>
          ) : (
            property.area_sqft && (
              <div className="flex items-center">
                <Square className="h-3 w-3 mr-1" />
                <span>{property.area_sqft} sq ft</span>
              </div>
            )
          )}
          {(property as any).bedrooms_options && (property as any).bedrooms_options.length > 0 ? (
            <div className="flex items-center">
              <Bed className="h-3 w-3 mr-1" />
              <span>{formatBedroomOptions((property as any).bedrooms_options as number[])}</span>
            </div>
          ) : (
            property.bedrooms && (
              <div className="flex items-center">
                <Bed className="h-3 w-3 mr-1" />
                <span>{property.bedrooms} bed</span>
              </div>
            )
          )}
          {property.bathrooms && (
            <div className="flex items-center">
              <Bath className="h-3 w-3 mr-1" />
              <span>{property.bathrooms} bath</span>
            </div>
          )}
        </div>

        {/* Floor/Unit Details */}
        {(property.floor_number || property.unit_number || property.total_floors) && (
          <div className="flex gap-4 text-sm text-muted-foreground mb-3">
            {property.unit_number && (
              <div>
                Unit: {property.unit_number}
              </div>
            )}
            {property.floor_number && (
              <div>
                Floor: {property.floor_number}
                {property.total_floors && `/${property.total_floors}`}
              </div>
            )}
            {!property.floor_number && property.total_floors && (
              <div>
                {property.total_floors} Floors
              </div>
            )}
          </div>
        )}

        {property.description && (
          <p className="text-sm text-foreground line-clamp-2 mb-3">
            {property.description}
          </p>
        )}
      </CardContent>

      <CardFooter className="p-4 pt-4 grid grid-cols-1 gap-3 border-t">
        <Link to={getPropertyUrl(property)} className="w-full">
          <Button variant="outline" className="w-full">
            <Eye className="h-4 w-4 mr-2" />
            View Details
          </Button>
        </Link>

        {property.price_on_request ? (
          <Dialog>
            <DialogTrigger asChild>
              <Button className="w-full">
                <IndianRupee className="h-4 w-4 mr-2" />
                Request Price
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Request Price Information</DialogTitle>
              </DialogHeader>
              <PriceRequestForm propertyId={property.id} propertyTitle={property.title} />
            </DialogContent>
          </Dialog>
        ) : (
          <AuthGatedDialog
            trigger={
              <Button className="w-full">
                <Phone className="h-4 w-4 mr-2" />
                Inquire Now
              </Button>
            }
            title="Property Inquiry"
          >
            <InquiryForm propertyId={property.id} propertyTitle={property.title} />
          </AuthGatedDialog>
        )}

        <div className="grid grid-cols-3 gap-2">
          <WhatsAppShare 
            property={property} 
            variant="compact" 
            size="sm"
            showText={false}
          />
          <EmailShare 
            property={property} 
            recipientEmail="hello@mnre.in" 
            recipientName="Property Team"
            variant="outline" 
            size="sm"
          />
          <PropertyShare property={property} />
        </div>
      </CardFooter>
    </Card>
  );
};
